<template>
  <StandardEmptyPageContainer>
    <LoadingCircle v-if="isLoading" />
    <v-row class="justify-center" v-else>
      <v-col sm="12" md="12" lg="8">
        <v-row>
          <v-col>
            <StandardCardContainer radius="small">
              <v-card-title class="text-left"
                >Welcome to Transparency Chain</v-card-title
              >
              <v-card-text class="text-left">
                <v-row class="mb-5">
                  <v-col cols="12" md="8">
                    <p>
                      {{ companiesString }}
                      {{ senderCompanies.length > 1 ? "have" : "has" }} sent you
                      a request to help them map their supply chain. Please
                      follow the steps below by clicking on the “Get
                      started”-button.
                    </p>
                  </v-col>
                </v-row>
                <v-row class="text-right">
                  <v-col>
                    <PActionButton
                      :append-icon="true"
                      :icon-size="'xs'"
                      appkit-icon="icon-right-chevron-fill"
                      @click="getStarted"
                      >Get started
                    </PActionButton>
                  </v-col>
                </v-row>
              </v-card-text>
            </StandardCardContainer>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <StandardCardContainer radius="small">
              <template #title>
                Register your company, then follow these steps:
              </template>
              <v-row class="mt-4">
                <v-col cols="12" sm="4">
                  <PIllustrationWithText
                    text="Fill out the questionnaire"
                    illustration="1"
                    overlaytext="1"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <PIllustrationWithText
                    text="Upload a list of relevant suppliers"
                    illustration="2"
                    overlaytext="2"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <PIllustrationWithText
                    text="Forward the questionnaire"
                    illustration="3"
                    overlaytext="3"
                  />
                </v-col>
              </v-row>
            </StandardCardContainer>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <StandardCardContainer
              radius="small"
              @clicked="show = 'aboutTheAct'"
              class="fill-height text-center"
              :clickable="true"
            >
              <PIllustrationWithText
                text="About the Transparency Act"
                imgsrc="/assets/pictogram-justice-legal.svg"
                size="xs"
                textsize="h3"
              />
            </StandardCardContainer>
          </v-col>
          <v-col cols="12" sm="6">
            <StandardCardContainer
              radius="small"
              @clicked="show = 'aboutTransparencyChain'"
              :clickable="true"
              class="fill-height text-center"
            >
              <PIllustrationWithText
                text="How does this work?"
                imgsrc="/assets/pictogram-roadmap.svg"
                size="xs"
                textsize="h3"
              />
            </StandardCardContainer>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </StandardEmptyPageContainer>
  <PModalBox
    :open-dialog="show != 'welcome'"
    :header="header"
    @close-dialog="show = 'welcome'"
    height="large"
  >
    <template #content>
      <HelpTexts
        :text-version="show == 'aboutTheAct' ? show : 'aboutTransparencyChain'"
      />
    </template>
    <template #rightbutton>
      <PPrimaryButton @click="show = 'welcome'">Close</PPrimaryButton>
    </template>
  </PModalBox>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import PIllustrationWithText from "@/components/custom/PIllustrationWithText.vue";
import HelpTexts from "@/views/help-view/HelpTexts.vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import PModalBox from "@/components/custom/PModalBox.vue";
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import { onBeforeMount, ref, type Ref, computed } from "vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { getSenderCompanies } from "@/services/questionnairesService";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";

const authenticationStore = useAuthenticationStore();
onBeforeMount(async () => {
  authenticationStore.inOnboarding = true;
  senderCompanies.value = await getSenderCompanies();
  isLoading.value = false;
});

const router = useRouter();
const show: Ref<"welcome" | "aboutTheAct" | "aboutTransparencyChain"> =
  ref("welcome");
const header = computed(() =>
  show.value == "aboutTheAct"
    ? "About the Transparency Act"
    : "About Transparency Chain"
);
const senderCompanies: Ref<string[]> = ref([]);
const companiesString = computed(() =>
  stringifyCompanyNames(senderCompanies.value)
);
const isLoading = ref(true);

function stringifyCompanyNames(companyNames: string[]) {
  if (companyNames.length == 1) {
    return companyNames[0];
  } else if (companyNames.length > 1) {
    return companyNames.slice(0, -1).join(", ") + ` and ${companyNames.at(-1)}`;
  } else {
    return "A customer";
  }
}
async function getStarted(): Promise<void> {
  router.push(RouterPath.RegisterSupplierCompany);
}
</script>
